import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import type { CellCoordinate, TableCellAdapter } from '../HourlyDataTable.types'

type UseTableCellNavigationOptions = {
  /** Strategy adapter — maps cell keys <-> grid coordinates */
  adapter: TableCellAdapter
  clearSelection: () => void
  clearValuesForSelection: (cellKeys: Set<string>) => void
  replaceSelection: (nextSelection: Set<string>) => void
  setAnchorCellKey: (key: string | null) => void
  inputRefs: React.MutableRefObject<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>
  bulkReplaceOnNextInputRef: React.MutableRefObject<boolean>
  selectedCellKeysRef: React.MutableRefObject<Set<string>>
  isCellEditable?: (cellKey: string) => boolean
}

type UseTableCellNavigationResult = {
  handleCellKeyDown: (cellKey: string, ev: ReactKeyboardEvent) => void
}

export function useTableCellNavigation({
  adapter,
  clearSelection,
  clearValuesForSelection,
  replaceSelection,
  setAnchorCellKey,
  inputRefs,
  bulkReplaceOnNextInputRef,
  selectedCellKeysRef,
  isCellEditable = () => true,
}: UseTableCellNavigationOptions): UseTableCellNavigationResult {
  const handleCellKeyDown = (cellKey: string, ev: ReactKeyboardEvent) => {
    const currentCoord = adapter.getCoordinate(cellKey)
    if (!currentCoord) {
      return
    }

    const selected = selectedCellKeysRef.current
    const isInMultiCellSelection = selected.size > 1 && selected.has(cellKey)

    if (!isCellEditable(cellKey) && !isInMultiCellSelection && (ev.key === 'Delete' || ev.key === 'Backspace')) {
      ev.preventDefault()
      return
    }

    if (ev.key === 'Delete') {
      ev.preventDefault()
      const selectedEditable = new Set([...selected].filter(isCellEditable))
      const toClear = isInMultiCellSelection ? selectedEditable : new Set([cellKey])
      clearValuesForSelection(toClear)
      return
    }

    if (ev.key === 'Backspace') {
      if (isInMultiCellSelection) {
        ev.preventDefault()
        clearValuesForSelection(new Set([...selected].filter(isCellEditable)))
      }
      return
    }

    if (ev.key === 'Escape') {
      ev.preventDefault()
      clearSelection()
      return
    }

    if (ev.key === 'Tab') {
      clearSelection()
      return
    }

    let nextCoord: CellCoordinate | null = null

    if (ev.key === 'Enter') {
      nextCoord = {
        rowIndex: currentCoord.rowIndex + (ev.shiftKey ? -1 : 1),
        columnIndex: currentCoord.columnIndex,
      }
    }

    if (!nextCoord) {
      return
    }

    const nextCellKey = adapter.getCellKey(nextCoord)
    if (!nextCellKey) {
      ev.preventDefault()
      replaceSelection(new Set([cellKey]))
      setAnchorCellKey(cellKey)
      bulkReplaceOnNextInputRef.current = false
      inputRefs.current[cellKey]?.focus()
      return
    }

    ev.preventDefault()
    replaceSelection(new Set([nextCellKey]))
    setAnchorCellKey(nextCellKey)
    bulkReplaceOnNextInputRef.current = false
    inputRefs.current[nextCellKey]?.focus()
  }

  return { handleCellKeyDown }
}
